import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Slider,
    Modal,
    Divider,
    Tabs,
    Tab,
    Tooltip,
    CircularProgress
} from '@mui/material';
import { DeleteIcon, CloseIcon, PlayArrowIcon, LinkIcon, VisibilityIcon, InsertPhotoOutlinedIcon } from 'icons/materialIcons';
import Cropper from 'react-easy-crop';
import getCroppedImg from 'utils/images/cropImageUtils';
import { uploadImageBlob } from 'apis/imageStorageApi';

// Enhanced utility functions for video thumbnail extraction
const extractYouTubeVideoId = (url) => {
    try {
        const parsed = new URL(url);
        if (parsed.hostname === 'youtu.be') {
            return parsed.pathname.slice(1);
        }
        if (parsed.hostname.includes('youtube.com')) {
            if (parsed.pathname.startsWith('/watch')) {
                return parsed.searchParams.get('v');
            }
            if (parsed.pathname.startsWith('/embed/')) {
                return parsed.pathname.split('/embed/')[1];
            }
            if (parsed.pathname.startsWith('/shorts/')) {
                return parsed.pathname.split('/shorts/')[1].split('/')[0];
            }
        }
    } catch {
        return null;
    }
    return null;
};

const getYouTubeThumbnail = (url) => {
    const id = extractYouTubeVideoId(url);
    if (id) {
        return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    }
    return null;
};

const extractVimeoVideoId = (url) => {
    try {
        const parsed = new URL(url);
        if (parsed.hostname.includes('vimeo.com')) {
            const match = parsed.pathname.match(/\/(\d+)/);
            if (match) return match[1];
        }
    } catch {
        return null;
    }
    return null;
};

const getVimeoThumbnail = async (url) => {
    try {
        const resp = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`);
        if (!resp.ok) return null;
        const data = await resp.json();
        return data.thumbnail_url || null;
    } catch {
        return null;
    }
};

const extractFirstFrameAsThumbnail = (videoUrl) => {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        video.src = videoUrl;
        video.crossOrigin = 'anonymous';
        video.muted = true;
        video.playsInline = true;
        video.currentTime = 0.1;

        const handleLoadedData = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth || 320;
                canvas.height = video.videoHeight || 240;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                resolve(dataUrl);
            } catch (error) {
                console.warn('Failed to extract frame:', error);
                resolve(null);
            } finally {
                cleanup();
            }
        };

        const handleError = () => {
            console.warn('Video failed to load for thumbnail extraction');
            resolve(null);
            cleanup();
        };

        const cleanup = () => {
            video.removeEventListener('loadeddata', handleLoadedData);
            video.removeEventListener('error', handleError);
            video.src = '';
        };

        video.addEventListener('loadeddata', handleLoadedData);
        video.addEventListener('error', handleError);

        // Timeout fallback
        setTimeout(() => {
            if (video.readyState < 2) {
                handleError();
            }
        }, 5000);
    });
};

const VIDEO_SIZE_LIMIT_MB = 50;
const VIDEO_SIZE_LIMIT_BYTES = VIDEO_SIZE_LIMIT_MB * 1024 * 1024;

const ProductsServicesForm = ({
    onDataChange,
    initialData = {},
    cardId,
    userId
}) => {
    // Form state
    const [productTitle, setProductTitle] = useState('');
    const [description, setDescription] = useState('');
    const [link, setLink] = useState('');
    const [ctaText, setCtaText] = useState('Learn More');

    // Image handling state
    const [images, setImages] = useState([]);
    const [imageForCropping, setImageForCropping] = useState(null);
    const [isImageCropperOpen, setIsImageCropperOpen] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    // Enhanced video handling state
    const [videos, setVideos] = useState([]);
    const [videoTabValue, setVideoTabValue] = useState(0);
    const [videoUrl, setVideoUrl] = useState('');
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [processingThumbnails, setProcessingThumbnails] = useState(new Set());

    // Control initialization
    const isInitializing = useRef(false);
    const lastDataRef = useRef(null);
    const initializationKey = useRef('');

    // Character limits
    const TITLE_LIMIT = 40;
    const DESCRIPTION_LIMIT = 160;

    // Helper functions for cropper
    const calculateInitialZoom = useCallback((mediaWidth, mediaHeight) => {
        const containerWidth = window.innerWidth * 0.9;
        const containerHeight = window.innerHeight * 0.9;
        const widthRatio = containerWidth / mediaWidth;
        const heightRatio = containerHeight / mediaHeight;
        return Math.min(widthRatio, heightRatio);
    }, []);

    const onMediaLoaded = useCallback((mediaSize) => {
        const initialZoom = calculateInitialZoom(mediaSize.width, mediaSize.height);
        setZoom(initialZoom);
        setCrop({ x: 0, y: 0 });
    }, [calculateInitialZoom]);

    // Enhanced video debugging function
    const debugVideoState = useCallback((videos, context) => {
        console.log(`=== VIDEO DEBUG [${context}] ===`);
        console.log('Total videos:', videos.length);
        videos.forEach((video, index) => {
            const isObject = typeof video === 'object' && video !== null;
            console.log(`Video ${index}:`, {
                type: typeof video,
                isObject,
                src: isObject ? video.src : video,
                thumbnail: isObject ? video.thumbnail : 'N/A (string format)',
                hasThumbnail: isObject ? !!video.thumbnail : false,
                thumbnailLength: isObject && video.thumbnail ? video.thumbnail.length : 0
            });
        });
        console.log('=================================');
    }, []);

    // Enhanced initialization with better video handling
    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            const dataKey = JSON.stringify(initialData);

            if (dataKey !== initializationKey.current) {
                console.log('ProductsServicesForm: Initializing with data:', initialData);

                isInitializing.current = true;
                initializationKey.current = dataKey;

                setProductTitle(initialData.productTitle || initialData.linkTitle || '');
                setDescription(initialData.description || '');
                setLink(initialData.link || initialData.ctaButton?.link || '');
                setCtaText(
                    initialData.ctaButton?.text !== undefined
                        ? initialData.ctaButton.text
                        : initialData.ctaText !== undefined
                            ? initialData.ctaText
                            : 'Learn More'
                );

                const initialImages = Array.isArray(initialData.images) ? [...initialData.images] : [];

                // Enhanced video initialization with proper object conversion
                const initialVideos = Array.isArray(initialData.videos)
                    ? initialData.videos.map((video, index) => {
                        if (typeof video === 'string') {
                            console.log(`Converting string video ${index} to object:`, video);
                            return { src: video, thumbnail: null };
                        } else if (typeof video === 'object' && video !== null) {
                            console.log(`Video ${index} already object:`, video);
                            return {
                                src: video.src || video,
                                thumbnail: video.thumbnail || null
                            };
                        } else {
                            console.warn(`Invalid video format at index ${index}:`, video);
                            return null;
                        }
                    }).filter(Boolean)
                    : [];

                console.log('Setting initial images:', initialImages);
                console.log('Setting initial videos:', initialVideos);

                setImages(initialImages);
                setVideos(initialVideos);

                // Debug video state after initialization
                debugVideoState(initialVideos, 'INITIALIZATION');

                setTimeout(() => {
                    isInitializing.current = false;
                    console.log('Initialization complete');
                }, 500);
            }
        }
    }, [initialData, debugVideoState]);

    // Enhanced data change notification with video debugging
    const notifyDataChange = useCallback(() => {
        if (isInitializing.current) {
            return;
        }

        debugVideoState(videos, 'NOTIFY_DATA_CHANGE');

        const data = {
            productTitle,
            description,
            link,
            images,
            videos,
            ctaButton: {
                text: ctaText,
                link: link
            },
            ctaText,
            linkTitle: productTitle,
            linkUrl: 'custom'
        };

        const dataString = JSON.stringify(data);
        if (lastDataRef.current !== dataString) {
            console.log('ProductsServicesForm: Notifying data change:', data);
            console.log('Videos in notification:', videos);
            lastDataRef.current = dataString;

            if (onDataChange && typeof onDataChange === 'function') {
                onDataChange(data);
            }
        }
    }, [productTitle, description, link, images, videos, ctaText, debugVideoState]);

    useEffect(() => {
        // Remove the 300ms delay to ensure immediate data synchronization
        notifyDataChange();
    }, [productTitle, description, link, images, videos, ctaText]);

    // Image upload handlers
    const handleImageUpload = (event) => {
        const files = Array.from(event.target.files);
        files.forEach((file) => {
            if (file && file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    setImageForCropping({
                        src: e.target.result,
                        file: file,
                        index: images.length
                    });
                    setIsImageCropperOpen(true);
                };
                reader.readAsDataURL(file);
            }
        });
    };

    const onImageCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleImageCropSave = async () => {
        if (!imageForCropping || !croppedAreaPixels) return;

        setUploadingImage(true);
        try {
            const croppedImageBlob = await getCroppedImg(
                imageForCropping.src,
                croppedAreaPixels,
                'rectangle'
            );

            const imageUrl = await uploadImageBlob(
                croppedImageBlob,
                null,
                'contact-link',
                'gallery',
                `${cardId}_${Date.now()}`
            );

            setImages(prev => {
                const newImages = [...prev, imageUrl];
                console.log('Updated images array:', newImages);
                return newImages;
            });
            setIsImageCropperOpen(false);
            setImageForCropping(null);
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Failed to upload image. Please try again.');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleCloseCropper = () => {
        setIsImageCropperOpen(false);
        setImageForCropping(null);
        setZoom(1);
    };

    // Enhanced video upload handler with automatic thumbnail generation
    const handleVideoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file || !file.type.startsWith('video/')) return;

        if (file.size > VIDEO_SIZE_LIMIT_BYTES) {
            alert(`Video file size must be ${VIDEO_SIZE_LIMIT_MB}MB or less. Please choose a smaller video.`);
            return;
        }

        setUploadingVideo(true);
        const tempVideoId = `temp_${Date.now()}`;

        try {
            const videoBlob = new Blob([file], { type: file.type });
            const videoUrl = await uploadImageBlob(
                videoBlob,
                null,
                'contact-link',
                'gallery',
                `${cardId}_${Date.now()}`
            );

            // Add video immediately without thumbnail
            const newVideoObject = { src: videoUrl, thumbnail: null };
            setVideos(prev => {
                const updated = [...prev, newVideoObject];
                debugVideoState(updated, 'UPLOAD_IMMEDIATE');
                return updated;
            });

            // Start thumbnail generation in background
            setProcessingThumbnails(prev => new Set([...prev, videoUrl]));

            try {
                console.log('Attempting to extract thumbnail from uploaded video:', videoUrl);
                const thumbnail = await extractFirstFrameAsThumbnail(videoUrl);

                if (thumbnail) {
                    console.log('Successfully extracted thumbnail, updating video object');
                    setVideos(prev => {
                        const updated = prev.map(video =>
                            video.src === videoUrl
                                ? { ...video, thumbnail }
                                : video
                        );
                        debugVideoState(updated, 'UPLOAD_WITH_THUMBNAIL');
                        return updated;
                    });
                } else {
                    console.log('Failed to extract thumbnail from uploaded video');
                }
            } catch (error) {
                console.error('Failed to extract thumbnail from uploaded video:', error);
            } finally {
                setProcessingThumbnails(prev => {
                    const updated = new Set(prev);
                    updated.delete(videoUrl);
                    return updated;
                });
            }

        } catch (error) {
            console.error('Error uploading video:', error);
            alert('Failed to upload video. Please try again.');
        } finally {
            setUploadingVideo(false);
        }
    };

    // Enhanced video URL handler with comprehensive thumbnail generation
    const handleAddVideoUrl = async () => {
        if (!videoUrl.trim()) {
            return;
        }

        const trimmedUrl = videoUrl.trim();
        let validUrl = trimmedUrl;

        try {
            new URL(trimmedUrl);
        } catch (e) {
            if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
                validUrl = `https://${trimmedUrl}`;
                try {
                    new URL(validUrl);
                } catch (e2) {
                    alert('Please enter a valid URL');
                    return;
                }
            } else {
                alert('Please enter a valid URL');
                return;
            }
        }

        console.log('Adding video URL:', validUrl);

        // Add video immediately without thumbnail
        const newVideoObject = { src: validUrl, thumbnail: null };
        setVideos(prev => {
            const updated = [...prev, newVideoObject];
            debugVideoState(updated, 'URL_IMMEDIATE');
            return updated;
        });

        // Start thumbnail generation in background
        setProcessingThumbnails(prev => new Set([...prev, validUrl]));

        try {
            let thumbnail = null;

            if (validUrl.includes('youtube.com') || validUrl.includes('youtu.be')) {
                console.log('Generating YouTube thumbnail...');
                thumbnail = getYouTubeThumbnail(validUrl);
                console.log('YouTube thumbnail result:', thumbnail);
            } else if (validUrl.includes('vimeo.com')) {
                console.log('Generating Vimeo thumbnail...');
                try {
                    thumbnail = await getVimeoThumbnail(validUrl);
                    console.log('Vimeo thumbnail result:', thumbnail);
                } catch (error) {
                    console.error('Failed to get Vimeo thumbnail:', error);
                }
            } else {
                console.log('Attempting to extract thumbnail from generic video URL...');
                try {
                    thumbnail = await extractFirstFrameAsThumbnail(validUrl);
                    console.log('Generic video thumbnail result:', thumbnail);
                } catch (error) {
                    console.error('Failed to extract thumbnail from generic video:', error);
                }
            }

            // Update video with thumbnail if generated
            if (thumbnail) {
                console.log('Updating video with thumbnail');
                setVideos(prev => {
                    const updated = prev.map(video =>
                        video.src === validUrl
                            ? { ...video, thumbnail }
                            : video
                    );
                    debugVideoState(updated, 'URL_WITH_THUMBNAIL');
                    return updated;
                });
            } else {
                console.log('No thumbnail generated for video URL');
            }

        } catch (error) {
            console.error('Error generating thumbnail:', error);
        } finally {
            setProcessingThumbnails(prev => {
                const updated = new Set(prev);
                updated.delete(validUrl);
                return updated;
            });
        }

        setVideoUrl('');
    };

    // Enhanced remove handlers with logging
    const removeImage = (indexToRemove) => {
        setImages(prev => {
            const newImages = prev.filter((_, index) => index !== indexToRemove);
            console.log('Removed image at index:', indexToRemove, 'New images array:', newImages);
            return newImages;
        });
    };

    const removeVideo = (indexToRemove) => {
        console.log('Removing video at index:', indexToRemove);
        setVideos(prev => {
            const updated = prev.filter((_, index) => index !== indexToRemove);
            debugVideoState(updated, 'REMOVE_VIDEO');
            return updated;
        });
    };

    // Character limit handlers
    const handleTitleChange = (e) => {
        const value = e.target.value;
        if (value.length <= TITLE_LIMIT) {
            console.log('🔍 ProductsServicesForm: Title changed to:', value);
            setProductTitle(value);
        }
    };

    const handleDescriptionChange = (e) => {
        const value = e.target.value;
        if (value.length <= DESCRIPTION_LIMIT) {
            console.log('🔍 ProductsServicesForm: Description changed to:', value);
            setDescription(value);
        }
    };

    const handleVideoUrlChange = (e) => {
        setVideoUrl(e.target.value);
    };

    const handleVideoUrlKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddVideoUrl();
        }
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '900px', margin: '0 auto' }}>
            {/* Product Title */}
            <Typography variant="body2" sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#4f4f4f',
                mb: 1
            }}>
                Heading/Title *
            </Typography>
            <TextField
                fullWidth
                size="small"
                placeholder="e.g., Web Development Service"
                value={productTitle}
                onChange={handleTitleChange}
                sx={{
                    mb: 1,
                    backgroundColor: '#f7f7f7',
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { border: 'none' },
                        '&:hover fieldset': { border: 'none' },
                        '&.Mui-focused fieldset': { border: 'none' },
                        '& input::placeholder': {
                            fontSize: '0.8rem',
                            fontWeight: '300',
                        },
                    },
                }}
            />
            <Typography
                variant="caption"
                sx={{ width: '100%', textAlign: 'right', pl: 0, ml: 0, marginLeft: 0, mt: 0.5, mb: 2, color: '#828282', fontSize: '0.7rem', display: 'block' }}
            >
                {productTitle.length}/{TITLE_LIMIT} characters
            </Typography>

            {/* Description */}
            <Typography variant="body2" sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#4f4f4f',
                mb: 1
            }}>
                Description
            </Typography>
            <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                placeholder="Describe your product or service..."
                value={description}
                onChange={handleDescriptionChange}
                sx={{
                    mb: 1,
                    backgroundColor: '#f7f7f7',
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { border: 'none' },
                        '&:hover fieldset': { border: 'none' },
                        '&.Mui-focused fieldset': { border: 'none' },
                        '& input::placeholder': {
                            fontSize: '0.8rem',
                            fontWeight: '300',
                        },
                    },
                }}
            />
            <Typography
                variant="caption"
                sx={{ width: '100%', textAlign: 'right', pl: 0, ml: 0, marginLeft: 0, mt: 0.5, mb: 2, color: '#828282', fontSize: '0.7rem', display: 'block' }}
            >
                {description.length}/{DESCRIPTION_LIMIT} characters
            </Typography>

            {/* Link */}
            <Typography variant="body2" sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#4f4f4f',
                mb: 1
            }}>
                Link
            </Typography>
            <TextField
                fullWidth
                size="small"
                placeholder="https://..."
                value={link}
                onChange={(e) => {
                    console.log('🔍 ProductsServicesForm: Link changed to:', e.target.value);
                    setLink(e.target.value);
                }}
                sx={{
                    mb: 2,
                    backgroundColor: '#f7f7f7',
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { border: 'none' },
                        '&:hover fieldset': { border: 'none' },
                        '&.Mui-focused fieldset': { border: 'none' },
                        '& input::placeholder': {
                            fontSize: '0.8rem',
                            fontWeight: '300',
                        },
                    },
                }}
            />

            {/* Call-to-Action Text */}
            <Typography variant="body2" sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#4f4f4f',
                mb: 1
            }}>
                Call to Action Text
            </Typography>
            <TextField
                fullWidth
                size="small"
                placeholder="e.g., Get Quote, Learn More"
                value={ctaText}
                onChange={(e) => {
                    console.log('🔍 ProductsServicesForm: CTA text changed to:', e.target.value);
                    setCtaText(e.target.value);
                }}
                sx={{
                    mb: 2,
                    backgroundColor: '#f7f7f7',
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { border: 'none' },
                        '&:hover fieldset': { border: 'none' },
                        '&.Mui-focused fieldset': { border: 'none' },
                        '& input::placeholder': {
                            fontSize: '0.8rem',
                            fontWeight: '300',
                        },
                    },
                }}
            />

            <Divider sx={{ my: 2 }} />

            {/* Media Type Label */}
            <Typography variant="body2" sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#4f4f4f',
                mb: 2
            }}>
                Media Content
            </Typography>

            {/* Images Section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#4f4f4f',
                    mb: 1.5
                }}>
                    Images ({images.length})
                </Typography>

                <Box
                    sx={{
                        position: 'relative',
                        width: 100,
                        height: 100,
                        mb: 2
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            borderRadius: 1,
                            backgroundColor: '#f2f2f2',
                            border: '1px solid #dbdbdb',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            padding: 1,
                            '&:hover': {
                                backgroundColor: '#eeeeee',
                            },
                        }}
                        component="label"
                    >
                        <InsertPhotoOutlinedIcon sx={{ fontSize: '28px', color: '#828282', marginBottom: '5px' }} />
                        <Typography
                            variant="body2"
                            sx={{ fontSize: '0.7rem', fontWeight: 400, color: '#212121' }}
                        >
                            <span style={{ color: 'blue' }}>Select</span> file or drag and drop here
                        </Typography>

                        <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                            disabled={uploadingImage}
                            style={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                top: 0,
                                left: 0,
                                opacity: 0,
                                cursor: 'pointer',
                            }}
                        />
                    </Box>
                </Box>

                {images.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {images.map((imageUrl, index) => (
                            <Box
                                key={index}
                                sx={{
                                    position: 'relative',
                                    width: 80,
                                    height: 80,
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    border: '1px solid #e0e0e0',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                <img
                                    src={imageUrl}
                                    alt={`Product ${index + 1}`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => removeImage(index)}
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        backgroundColor: 'rgba(255, 255, 255, 1)',
                                        color: '#828282',
                                        width: 20,
                                        height: 20,
                                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 1)'
                                        }
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            {/* Enhanced Videos Section */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#4f4f4f',
                    mb: 1.5
                }}>
                    Videos ({videos.length})
                </Typography>

                <Box sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    mb: 2,
                    backgroundColor: '#f7f7f7',
                    borderRadius: 1
                }}>
                    <Tabs
                        value={videoTabValue}
                        onChange={(e, newValue) => setVideoTabValue(newValue)}
                        aria-label="video input tabs"
                        sx={{
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: '#4f4f4f',
                            }
                        }}
                    >
                        <Tab label="Upload File" />
                        <Tab label="Add URL" />
                    </Tabs>
                </Box>

                {videoTabValue === 0 && (
                    <Button
                        component="label"
                        variant="outlined"
                        size="small"
                        disabled={uploadingVideo}
                        sx={{
                            mb: 2,
                            textTransform: 'none',
                            borderColor: '#dedede',
                            color: '#4f4f4f',
                            backgroundColor: '#f7f7f7',
                            '&:hover': {
                                backgroundColor: '#eeeeee',
                                borderColor: '#dedede',
                            },
                        }}
                    >
                        {uploadingVideo ? 'Uploading...' : 'Upload Video'}
                        <input
                            type="file"
                            hidden
                            accept="video/*"
                            onChange={handleVideoUpload}
                        />
                    </Button>
                )}

                {videoTabValue === 1 && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Enter video URL... (YouTube, Vimeo, etc.)"
                            value={videoUrl}
                            onChange={handleVideoUrlChange}
                            onKeyPress={handleVideoUrlKeyPress}
                            sx={{
                                flex: 1,
                                backgroundColor: '#f7f7f7',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { border: 'none' },
                                    '&:hover fieldset': { border: 'none' },
                                    '&.Mui-focused fieldset': { border: 'none' },
                                    '& input::placeholder': {
                                        fontSize: '0.8rem',
                                        fontWeight: '300',
                                    },
                                },
                            }}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleAddVideoUrl}
                            disabled={!videoUrl.trim()}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#dedede',
                                color: '#4f4f4f',
                                backgroundColor: '#f7f7f7',
                                '&:hover': {
                                    backgroundColor: '#eeeeee',
                                    borderColor: '#dedede',
                                },
                            }}
                        >
                            Add
                        </Button>
                    </Box>
                )}

                {/* Enhanced Video Display with Thumbnail Status */}
                {videos.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {videos.map((video, index) => {
                            const videoSrc = typeof video === 'object' ? video.src : video;
                            const videoThumbnail = typeof video === 'object' ? video.thumbnail : null;
                            const isProcessing = processingThumbnails.has(videoSrc);

                            return (
                                <Box
                                    key={`video-${index}-${videoSrc}`}
                                    sx={{
                                        position: 'relative',
                                        width: 140,
                                        height: 90,
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        border: '1px solid #e0e0e0',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        backgroundColor: '#f5f5f5'
                                    }}
                                >
                                    {/* Enhanced thumbnail display with loading state */}
                                    {videoThumbnail ? (
                                        <img
                                            src={videoThumbnail}
                                            alt={`Video ${index + 1} thumbnail`}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                            onError={(e) => {
                                                console.warn(`Thumbnail failed to load for video ${index}:`, videoThumbnail);
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: '#f0f0f0',
                                                flexDirection: 'column'
                                            }}
                                        >
                                            {isProcessing ? (
                                                <>
                                                    <CircularProgress size={20} sx={{ mb: 0.5 }} />
                                                    <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                                                        Processing...
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <PlayArrowIcon sx={{ fontSize: '2rem', color: '#666' }} />
                                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666' }}>
                                                        No thumbnail
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                    )}

                                    {/* Video hidden element for fallback thumbnail generation */}
                                    <video
                                        src={videoSrc}
                                        style={{ display: 'none' }}
                                        muted
                                        preload="metadata"
                                    />

                                    {/* Play icon overlay */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            backgroundColor: 'rgba(0,0,0,0.7)',
                                            borderRadius: '50%',
                                            width: 32,
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => window.open(videoSrc, '_blank')}
                                    >
                                        <PlayArrowIcon sx={{ color: 'white', fontSize: '1rem' }} />
                                    </Box>

                                    {/* Remove Button */}
                                    <IconButton
                                        size="small"
                                        onClick={() => removeVideo(index)}
                                        sx={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            backgroundColor: 'rgba(255, 255, 255, 1)',
                                            color: '#828282',
                                            width: 20,
                                            height: 20,
                                            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 1)'
                                            }
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: 12 }} />
                                    </IconButton>

                                    {/* Debug overlay for development */}
                                    {process.env.NODE_ENV === 'development' && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                backgroundColor: 'rgba(0,0,0,0.8)',
                                                color: 'white',
                                                fontSize: '0.6rem',
                                                padding: '2px 4px',
                                                textAlign: 'center'
                                            }}
                                        >
                                            {videoThumbnail ? '✓ Thumb' : isProcessing ? 'Processing...' : '✗ No thumb'}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>

            {/* Image Cropper Modal */}
            <Modal
                open={isImageCropperOpen}
                onClose={handleCloseCropper}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backdropFilter: 'blur(7px)',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }}
            >
                <Box
                    sx={{
                        outline: 'none',
                        width: '90vw',
                        maxWidth: '900px',
                        height: '90vh',
                        maxHeight: '700px',
                    }}
                >
                    {imageForCropping && (
                        <>
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: 'calc(100% - 100px)',
                                    marginBottom: 2,
                                }}
                            >
                                <Cropper
                                    image={imageForCropping.src}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={16 / 9}
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={onImageCropComplete}
                                    onMediaLoaded={onMediaLoaded}
                                    objectFit="contain"
                                    centerImage={true}
                                    showGrid={false}
                                    minZoom={0.1}
                                    maxZoom={3}
                                    restrictPosition={false}
                                />
                            </Box>

                            <Box sx={{ textAlign: 'center' }}>
                                <Slider
                                    value={zoom}
                                    onChange={(e, newValue) => setZoom(newValue)}
                                    min={0.1}
                                    max={3}
                                    step={0.01}
                                    valueLabelDisplay="auto"
                                    defaultValue={1}
                                    sx={{
                                        marginBottom: 2,
                                        width: '80%',
                                        margin: '0 auto 20px auto'
                                    }}
                                />
                                <div>
                                    <Button
                                        onClick={handleCloseCropper}
                                        variant="contained"
                                        sx={{ marginRight: 2 }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleImageCropSave}
                                        variant="contained"
                                        color="primary"
                                        disabled={uploadingImage}
                                    >
                                        {uploadingImage ? 'Uploading...' : 'Crop'}
                                    </Button>
                                </div>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>
        </Box>
    );
};

export default ProductsServicesForm;